import { useEffect, useRef, useState } from "react";
import { MdClose, MdSend, MdDeleteOutline, MdPictureAsPdf, MdInfoOutline, MdAttachFile, MdVolumeUp, MdStop, MdVpnKey, MdCheckCircle } from "react-icons/md";

import agentButtonImg from "../mini_agent_ai.png";       // przycisk otwierający okno asystenta
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Config from "../Config";

import "./AIDialog.css";


// linki z odpowiedzi AI otwieramy w nowej karcie
const mdComponents = {
    a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
};


// otwiera plik wygenerowany przez agenta (PDF w nowej karcie, pozostałe – pobranie)
function openAttachment(att)
{
    const url = att.mime === "application/pdf"
                ? Config.makeBase64PDFToBlobUrl(att.base64)
                : "data:" + att.mime + ";base64," + att.base64;

    const a = document.createElement("a");
    a.href = url;
    if(att.mime === "application/pdf") a.target = "_blank";
    else a.download = att.name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
}


async function apiPost(path, body, userData)
{
    return fetch( Config.url + path, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': userData.apiKey,
            '_p_przedszkole_id_': userData.p_przedszkole_id,
            '_p_dziecko_id_': userData.p_dziecko_id,
            '_sec_key_': Config.genXKey(),
        }
    }).then(r => r.json());
}


// stan limitów tokenów placówki: { input: { limit, wykorzystano, pozostalo }, output: { ... } } – bez kosztów
async function fetchUsage(userData)
{
    const res = await apiPost('/rest_v1_ai,usage', {}, userData);
    return (res && res.status == 'OK') ? res.result : null;
}

// czy asystent AI jest aktywny w placówce (rest_v1_ai,agent_active: result 1 / 0)
async function fetchAgentActive(userData)
{
    const res = await apiPost('/rest_v1_ai,agent_active', {}, userData);
    return res && res.status == 'OK' && String(res.result) === '1';
}

const fmt = (n) => Number(n || 0).toLocaleString("pl-PL");


// jeden pasek limitu: u = { limit, wykorzystano, pozostalo }
function UsageRow({ label, u })
{
    const over = u.pozostalo <= 0;

    return (
        <div className={"ai_usage_row" + (over ? " ai_usage_over" : "")}>
            <div className="ai_usage_text">
                <span>{label}: wykorzystano <b>{fmt(u.wykorzystano)}</b> z <b>{fmt(u.limit)}</b></span>
                <span>pozostało <b>{fmt(u.pozostalo)}</b></span>
            </div>
            <div className="ai_usage_bar">
                <div style={{ width: Math.min(100, u.limit > 0 ? u.wykorzystano / u.limit * 100 : 100) + "%" }}></div>
            </div>
        </div>
    )
}


// widok danych z bazy (narzędzia show_* agenta) – dane nie przechodzą przez AI, backend przysyła je gotowe:
// { tytul, bloki: [ { typ: 'karta', tytul, wiersze: [[etykieta, wartość]] } | { typ: 'tabela', tytul, kolumny: [], wiersze: [[]] } ] }
function AIDisplay({ display })
{
    return (
        <div className="ai_display">
            { display.tytul && <div className="ai_display_title">{display.tytul}</div> }
            {
                (display.bloki || []).map((b, bx) =>
                    <div key={bx} className="ai_display_block">
                        { b.tytul && <div className="ai_display_subtitle">{b.tytul}</div> }
                        {
                            b.typ === "tabela"
                            ? ( b.wiersze.length === 0
                                ? <div className="ai_display_empty">Brak danych</div>
                                : <div className="ai_display_scroll">
                                      <table className="ai_display_table">
                                          <thead><tr>{ b.kolumny.map((k, kx) => <th key={kx}>{k}</th>) }</tr></thead>
                                          <tbody>
                                              { b.wiersze.map((w, wx) => <tr key={wx}>{ w.map((c, cx) => <td key={cx}>{c}</td>) }</tr>) }
                                          </tbody>
                                      </table>
                                  </div> )
                            : <table className="ai_display_card">
                                  <tbody>
                                      { b.wiersze.map((w, wx) => <tr key={wx}><th>{w[0]}</th><td>{w[1]}</td></tr>) }
                                  </tbody>
                              </table>
                        }
                    </div>
                )
            }
        </div>
    )
}


// załączniki do pytania – jak InputUploaderAttach (rest_v1,upload → plg_pliki); zwraca { name, crypt }
// AI odczytuje PDF, obrazy i pliki tekstowe; dokumenty biurowe i filmy tylko do wysłania w wiadomości / publikacji na tablicy
const AI_FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods,.mp4,.mov,.crt,.cer,.pem,.key,.pkey";   // certyfikaty / klucze → panel KSeF (bez wgrywania)
const AI_FILES_MAX   = 5;   // jak AI_FILES_PER_MSG w backendzie

async function uploadFile(file, userData)
{
    const data = new FormData();
    data.append('typ', 'ai_asystent');
    data.append('typ_id', -1);
    data.append('remove', '0');
    data.append('crypt', '');
    data.append('file0', file);

    const res = await fetch(Config.master + '/rest_v1,upload', {
        method: 'POST',
        body: data,
        headers: {
            'X-Api-Key': userData.apiKey,
            '_p_przedszkole_id_': userData.p_przedszkole_id,
            '_p_dziecko_id_': userData.p_dziecko_id,
            '_sec_key_': Config.genXKey(),
        }
    }).then(r => r.json());

    if(!res || res.status !== 'OK' || !res.param || !res.param.crypt)
        throw new Error("Nie udało się wgrać pliku " + file.name);

    return { name: res.param.old_name || file.name, crypt: res.param.crypt };
}


// KSeF: certyfikat (.crt / .cer / .pem) i klucz prywatny (.key / .pkey / .pem) czytane tu, w przeglądarce – bez wgrywania na serwer plików
// i bez historii rozmowy; razem z hasłem trafiają tylko do backendu eLizak (pole "ksef" żądania chat), nigdy do modelu AI
const KSEF_FILE_EXT   = [ "crt", "cer", "pem", "key", "pkey" ];
const KSEF_FILE_OTHER = [ "p12", "pfx" ];   // kontener z kluczem – nieobsługiwany (KSeF wydaje osobne pliki .crt i .key)
const KSEF_EMPTY      = { crt: "", crtName: "", key: "", keyName: "", pass: "" };
const KSEF_SECRET_TEXT = /-----BEGIN [A-Z ]*PRIVATE KEY-----/;   // klucz prywatny wklejony w treść pytania

const fileExt = (name) => String(name || "").split(".").pop().toLowerCase();

// plik certyfikatu / klucza → { kind: 'crt' | 'key', text }; certyfikat binarny (DER) – jako base64 (backend dodaje nagłówek PEM)
function readKsefFile(file)
{
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Nie udało się odczytać pliku " + file.name));
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result);
            const text  = new TextDecoder("utf-8").decode(bytes);
            const ext   = fileExt(file.name);

            if(text.includes("-----BEGIN"))
            {
                if(/PRIVATE KEY-----/.test(text)) return resolve({ kind: "key", text });
                if(/CERTIFICATE-----/.test(text)) return resolve({ kind: "crt", text });
                return reject(new Error("Plik " + file.name + " nie jest certyfikatem ani kluczem prywatnym."));
            }
            if(ext === "key" || ext === "pkey")
                return reject(new Error("Klucz prywatny " + file.name + " ma nieobsługiwany format – potrzebny plik .key zapisany przy generowaniu certyfikatu KSeF."));

            let bin = "";
            bytes.forEach(b => { bin += String.fromCharCode(b); });
            resolve({ kind: "crt", text: btoa(bin) });
        };
        reader.readAsArrayBuffer(file);
    });
}


// panel KSeF w oknie asystenta – wybór certyfikatu i klucza, hasło klucza; dane zostają w eLizak (nie trafiają do AI)
function KsefPanel({ ksef, busy, onPick, onPass, onClear, onClose, onReady })
{
    const crtRef = useRef(null);
    const keyRef = useRef(null);
    const ready  = ksef.crt !== "" && ksef.key !== "" && ksef.pass !== "";

    const pick = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if(f) onPick(f); };

    return (
        <div className="ai_ksef" role="group" aria-label="Podłączenie do KSeF">
            <div className="ai_ksef_head">
                <MdVpnKey /> <span>Podłączenie do KSeF – certyfikat</span>
                <button type="button" className="ai_btn_icon" title="Zamknij panel (wczytane dane zostają)" onClick={onClose}><MdClose /></button>
            </div>
            <div className="ai_ksef_info">
                Pliki i hasło <b>nie są przekazywane do asystenta AI</b> – sprawdza je i zapisuje serwer eLizak. Nie wklejaj ich w treść wiadomości.
            </div>

            <input ref={crtRef} type="file" accept=".crt,.cer,.pem" style={{ display: "none" }} onChange={pick} />
            <input ref={keyRef} type="file" accept=".key,.pkey,.pem" style={{ display: "none" }} onChange={pick} />

            <div className="ai_ksef_row">
                <span className="ai_ksef_label">1. Certyfikat (.crt)</span>
                <button type="button" className="ai_ksef_btn" onClick={() => crtRef.current && crtRef.current.click()}>wybierz plik</button>
                { ksef.crt !== "" && <span className="ai_ksef_ok" title={ksef.crtName}><MdCheckCircle /> {ksef.crtName}</span> }
            </div>
            <div className="ai_ksef_row">
                <span className="ai_ksef_label">2. Klucz prywatny (.key)</span>
                <button type="button" className="ai_ksef_btn" onClick={() => keyRef.current && keyRef.current.click()}>wybierz plik</button>
                { ksef.key !== "" && <span className="ai_ksef_ok" title={ksef.keyName}><MdCheckCircle /> {ksef.keyName}</span> }
            </div>
            <div className="ai_ksef_row">
                <span className="ai_ksef_label">3. Hasło klucza prywatnego</span>
                <input type="password" className="ai_ksef_pass" autoComplete="new-password" placeholder="hasło nadane przy generowaniu certyfikatu"
                       value={ksef.pass} onChange={(e) => onPass(e.target.value)} />
            </div>

            <div className="ai_ksef_btns">
                <button type="button" className="ai_ksef_clear" onClick={onClear}>Wyczyść</button>
                <button type="button" className="ai_ksef_ready" disabled={!ready || busy} onClick={onReady}
                        title={ready ? "Asystent sprawdzi pliki i hasło" : "Wczytaj certyfikat, klucz i wpisz hasło"}>Gotowe – sprawdź</button>
            </div>
        </div>
    )
}


// zabezpieczenie: token anonimizacji (-#tabela-kolumna-id#-, także zapisany niedokładnie) nigdy nie trafia na ekran –
// backend odkodowuje tokeny, a tu zostaje tylko taki, którego nie dało się odkodować
const AI_TOKEN_SCREEN = /-?#[a-z0-9_]+-[a-z0-9_]+-\d+#-?/g;
const hideTokens = (text) => typeof text === "string" ? text.replace(AI_TOKEN_SCREEN, "#####") : text;


// odczytywanie odpowiedzi na głos (Web Speech API) – tylko na komputerze (PC / Mac) i tylko gdy przeglądarka
// obsługuje syntezę mowy i ma polski głos; na telefonach i tabletach przycisk się nie pojawia
const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window && typeof window.SpeechSynthesisUtterance === "function";

function isDesktop()
{
    const ua = navigator.userAgent || "";
    if(/Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(ua)) return false;
    if(/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return false;   // iPadOS przedstawia się jako Mac
    // główne urządzenie wskazujące to mysz / touchpad (nie palec)
    return !window.matchMedia || window.matchMedia("(pointer: fine)").matches;
}

// polski głos – preferowane naturalne głosy online (Edge: "Natural", Chrome: "Google"), potem dowolny polski
function pickPolishVoice()
{
    const pl = window.speechSynthesis.getVoices().filter(v => v.lang && v.lang.toLowerCase().replace("_", "-").startsWith("pl"));
    return pl.find(v => /natural|online/i.test(v.name)) || pl.find(v => /google/i.test(v.name)) || pl[0] || null;
}

// Markdown → zwykły tekst do przeczytania (bez znaczników, kodu, adresów URL i ukrytych tokenów)
function speechText(md)
{
    return String(md || "")
        .replace(/```[\s\S]*?```/g, " ")              // bloki kodu
        .replace(/`([^`]*)`/g, "$1")                  // kod w linii
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")         // obrazki
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")       // linki – zostaje opis
        .replace(/https?:\/\/\S+/g, " ")              // gołe adresy
        .replace(/^\s*\|?\s*:?-{2,}.*$/gm, " ")        // separator nagłówka tabeli
        .replace(/\|/g, ", ")                         // komórki tabeli
        .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, "")   // nagłówki, cytaty, listy
        .replace(/(\*\*|__|~~|\*|_)(\S[\s\S]*?\S|\S)\1/g, "$2") // pogrubienie, kursywa, przekreślenie
        .replace(/#{3,}/g, " ")                       // ukryte dane (#####)
        .replace(/[ \t]+/g, " ")
        .trim();
}

// tekst dzielony na zdania / linie – Chrome przerywa zbyt długie wypowiedzi (ok. 15 s)
function speechChunks(text)
{
    return (text.match(/[^.!?…\n]+[.!?…]*/g) || [])
        .map(s => s.trim())
        .filter(s => /[0-9A-Za-zÀ-ž]/.test(s));
}


// wysyła historię rozmowy do agenta AI, zwraca tekst odpowiedzi;
// ksef – dane panelu KSeF (certyfikat, klucz, hasło): osobne pole żądania, poza historią – backend nie przekazuje go do AI
async function askAI(history, userData, ksef)
{
    const chat = history.filter(m => m.role === "user" || m.role === "assistant");

    const req = {
        // tylko tekst – załączniki (PDF) nie wracają do backendu;
        // odpowiedzi asystenta w postaci z tokenami, żeby dane osobowe nie wracały do AI jawnie
        // załączniki użytkownika – tylko crypt (plg_pliki), backend sam wczytuje treść pliku
        messages: chat.map(m => ({ role: m.role, content: m.raw !== undefined ? m.raw : m.content,
                                   ...(m.files && m.files.length > 0 ? { files: m.files.map(f => f.crypt) } : {}) })),
        // mapa tokenów z poprzednich odpowiedzi – backend podmieni je w nowej odpowiedzi
        tokens: Object.assign({}, ...chat.map(m => m.tokens || {}))
    };
    if(ksef && (ksef.crt !== "" || ksef.key !== "" || ksef.pass !== ""))
        req.ksef = { crt: ksef.crt, key: ksef.key, pass: ksef.pass };

    const res = await apiPost('/rest_v1_ai,chat', req, userData);

    if(res._sys_ == undefined)
        throw new Error("No correct response from server");

    if(res.status == 'ERROR')
    {
        const err = new Error(typeof res.result === "string" ? res.result : JSON.stringify(res.result));
        err.usage = res.usage;   // stan limitu – np. gdy limit został wyczerpany
        err.dataChanged = res.dane_zmienione === true;   // błąd mógł wystąpić już po zapisie danych
        err.agentOff    = res.agent_wylaczony === true;  // asystent wyłączony w placówce
        err.ksefSaved   = res.ksef_zapisano === true;    // certyfikat KSeF zapisany przed błędem
        throw err;
    }

    const changedBy = Array.isArray(res.result.zmienione_przez) ? res.result.zmienione_przez : [];

    return {
        usage: res.usage,
        content: hideTokens(res.result.content),
        raw: typeof res.result.content_raw === "string" ? res.result.content_raw : res.result.content,
        tokens: res.result.tokens && typeof res.result.tokens === "object" ? res.result.tokens : {},
        attachments: Array.isArray(res.result.attachments) ? res.result.attachments : [],
        displays: Array.isArray(res.result.displays) ? res.result.displays : [],   // widoki danych (show_*) – tylko do wyświetlenia, nie wracają do AI
        // agent zmienił dane (zapis przez narzędzie) – widoki panelu są nieaktualne; samo wyłączenie asystenta nie wymaga odświeżenia panelu
        dataChanged: res.result.dane_zmienione === true && changedBy.some(n => n !== "agent_disable"),
        // asystent został wyłączony w placówce (narzędzie agent_disable) – to ostatnia odpowiedź
        agentOff: changedBy.includes("agent_disable"),
        ksefForm:  res.result.ksef_formularz === true,   // asystent prosi o wczytanie certyfikatu / hasła – otwórz panel KSeF
        ksefSaved: res.result.ksef_zapisano === true     // certyfikat zapisany w ustawieniach – wyczyść panel KSeF
    };
}


function AIDialog(props) {

    const [active, setActive] = useState(false);   // asystent aktywny w placówce – do czasu odpowiedzi agent_active przycisk ukryty
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);   // { role: 'user' | 'assistant', content: '' }
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [usage, setUsage] = useState(null);   // limity tokenów placówki (wejściowe / wyjściowe)
    const [refreshNeeded, setRefreshNeeded] = useState(false);   // agent zmienił dane – wymagane odświeżenie strony
    const [files, setFiles] = useState([]);           // załączniki do następnego pytania: { name, crypt }
    const [uploading, setUploading] = useState(0);    // liczba plików w trakcie wgrywania
    const [agentOff, setAgentOff] = useState(false);  // asystent wyłączony w placówce w trakcie rozmowy – pole pytania zablokowane, po zamknięciu okna przycisk znika
    const fileRef = useRef(null);
    const [ttsVoice, setTtsVoice] = useState(null);     // polski głos do odczytywania odpowiedzi – null: przycisk "Przeczytaj" ukryty
    const [speakingIx, setSpeakingIx] = useState(-1);   // indeks odpowiedzi czytanej na głos (-1 – nic)
    const utterRef = useRef([]);                        // bieżące wypowiedzi – referencje chronią przed utratą zdarzeń (Chrome)
    const [ksef, setKsef] = useState(KSEF_EMPTY);       // panel KSeF: certyfikat, klucz prywatny, hasło – tylko w pamięci, nie w historii rozmowy
    const [ksefOpen, setKsefOpen] = useState(false);

    const limitReached = usage != null && usage.input != null && usage.output != null
                         && (usage.input.pozostalo <= 0 || usage.output.pozostalo <= 0);
    const blocked = limitReached || agentOff;   // pytania nie są przyjmowane

    const listRef = useRef(null);
    const inputRef = useRef(null);


    // aktywność asystenta w placówce – przy starcie i zmianie użytkownika / placówki; błąd = nieaktywny
    useEffect(() => {
        let anulowane = false;
        fetchAgentActive(props.userData)
            .then(a => { if(!anulowane) setActive(a); })
            .catch(() => { if(!anulowane) setActive(false); });
        return () => { anulowane = true; };
    }, [props.userData]);

    // głos do odczytywania odpowiedzi – tylko komputer + obsługa syntezy mowy + polski głos;
    // lista głosów ładuje się asynchronicznie (voiceschanged)
    useEffect(() => {
        if(!ttsSupported || !isDesktop()) return;

        const synth = window.speechSynthesis;
        const update = () => setTtsVoice(pickPolishVoice());
        update();
        synth.addEventListener("voiceschanged", update);

        return () => {
            synth.removeEventListener("voiceschanged", update);
            synth.cancel();
        };
    }, []);

    // zamknięcie okna lub wyczyszczenie rozmowy przerywa czytanie
    useEffect(() => {
        if(ttsSupported && (!open || messages.length === 0)) stopSpeaking();
    }, [open, messages.length]);   // eslint-disable-line react-hooks/exhaustive-deps

    // przewijanie do ostatniej wiadomości
    useEffect(() => {
        if(listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, busy, open]);

    // stan limitu tokenów – odświeżany przy każdym otwarciu okna
    useEffect(() => {
        if(!open) return;
        fetchUsage(props.userData).then(u => { if(u) setUsage(u); }).catch(() => {});
    }, [open, props.userData]);

    // focus na pole + zamykanie Esc
    useEffect(() => {
        if(!open) return;

        if(inputRef.current) inputRef.current.focus();

        const onKey = (e) => { if(e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // asystent wyłączony w trakcie rozmowy – po zamknięciu okna przycisk znika (jak przy agent_active = 0)
    useEffect(() => {
        if(!open && agentOff) setActive(false);
    }, [open, agentOff]);


    const clearKsef = () => { setKsef(KSEF_EMPTY); setKsefOpen(false); };

    // textOverride – wiadomość wysyłana przyciskiem (np. "Gotowe – sprawdź" w panelu KSeF), bez pola pytania i załączników
    const send = async (textOverride) => {

        const own  = typeof textOverride === "string";
        const text = own ? textOverride : prompt.trim();
        const att  = own ? [] : files;
        if((text === "" && att.length === 0) || busy || uploading > 0 || blocked) return;

        // klucz prywatny wklejony w pytanie – nie wysyłamy (trafiłby do AI); wczytuje się go w panelu KSeF
        if(KSEF_SECRET_TEXT.test(text))
        {
            setKsefOpen(true);
            setMessages(m => [...m, { role: "error", content: "Nie wysłano: wiadomość zawiera klucz prywatny. Klucz i certyfikat KSeF wczytaj w panelu KSeF poniżej – nie trafią wtedy do asystenta AI." }]);
            return;
        }

        const history = [...messages, { role: "user", content: text, ...(att.length > 0 ? { files: att } : {}) }];
        setMessages(history);
        if(!own) { setPrompt(""); setFiles([]); }
        setBusy(true);

        try
        {
            const answer = await askAI(history, props.userData, ksef);
            if(answer.usage) setUsage(answer.usage);
            if(answer.dataChanged) setRefreshNeeded(true);
            if(answer.agentOff) setAgentOff(true);
            if(answer.ksefSaved) clearKsef();
            else if(answer.ksefForm) setKsefOpen(true);
            setMessages(m => [...m, { role: "assistant", content: answer.content, raw: answer.raw, tokens: answer.tokens, attachments: answer.attachments, displays: answer.displays }]);
        }
        catch(err)
        {
            if(err && err.usage) setUsage(err.usage);
            if(err && err.dataChanged) setRefreshNeeded(true);
            if(err && err.agentOff) setAgentOff(true);
            if(err && err.ksefSaved) clearKsef();
            setMessages(m => [...m, { role: "error", content: "Błąd: " + (err && err.message ? err.message : err) }]);
        }
        finally
        {
            setBusy(false);
            if(inputRef.current) inputRef.current.focus();
        }
    }

    // plik certyfikatu / klucza KSeF → panel KSeF (odczyt w przeglądarce, bez wgrywania na serwer)
    const onKsefFile = async (file) => {
        try
        {
            const f = await readKsefFile(file);
            setKsef(k => f.kind === "crt" ? { ...k, crt: f.text, crtName: file.name } : { ...k, key: f.text, keyName: file.name });
        }
        catch(err)
        {
            setMessages(m => [...m, { role: "error", content: "Błąd: " + (err && err.message ? err.message : err) }]);
        }
        setKsefOpen(true);
    }

    // wybór plików → wgranie każdego osobno (rest_v1,upload); błąd jednego pliku nie blokuje pozostałych.
    // Certyfikaty i klucze nie są wgrywane – trafiają do panelu KSeF
    const onPickFiles = async (e) => {

        const all = Array.from(e.target.files || []);
        e.target.value = "";

        for(const file of all)
        {
            if(KSEF_FILE_EXT.includes(fileExt(file.name))) await onKsefFile(file);
            else if(KSEF_FILE_OTHER.includes(fileExt(file.name)))
                setMessages(m => [...m, { role: "error", content: "Plik " + file.name + " (.p12 / .pfx) nie jest obsługiwany – do KSeF potrzebne są osobne pliki certyfikatu (.crt) i klucza prywatnego (.key)." }]);
        }

        const picked = all.filter(f => !KSEF_FILE_EXT.includes(fileExt(f.name)) && !KSEF_FILE_OTHER.includes(fileExt(f.name)))
                          .slice(0, Math.max(0, AI_FILES_MAX - files.length));
        if(picked.length === 0) return;

        setUploading(n => n + picked.length);
        for(const file of picked)
        {
            try
            {
                const f = await uploadFile(file, props.userData);
                setFiles(list => [...list, f]);
            }
            catch(err)
            {
                setMessages(m => [...m, { role: "error", content: "Błąd: " + (err && err.message ? err.message : err) }]);
            }
            finally
            {
                setUploading(n => n - 1);
            }
        }
    }

    const stopSpeaking = () => {
        utterRef.current = [];
        window.speechSynthesis.cancel();
        setSpeakingIx(-1);
    }

    // odczytanie odpowiedzi na głos; ponowne kliknięcie przy czytanej odpowiedzi – zatrzymanie
    const toggleSpeak = (ix, content) => {

        if(speakingIx === ix) { stopSpeaking(); return; }

        const synth = window.speechSynthesis;
        synth.cancel();

        const chunks = speechChunks(speechText(content));
        if(chunks.length === 0 || !ttsVoice) { setSpeakingIx(-1); return; }

        const list = chunks.map(text => {
            const u = new SpeechSynthesisUtterance(text);
            u.voice = ttsVoice;
            u.lang  = ttsVoice.lang;
            u.rate  = 1;
            return u;
        });
        // koniec ostatniego fragmentu lub błąd – czytanie zakończone (o ile w międzyczasie nie zaczęto innego)
        const done = () => { if(utterRef.current === list) { utterRef.current = []; setSpeakingIx(-1); } };
        list[list.length - 1].onend = done;
        list.forEach(u => { u.onerror = done; });

        utterRef.current = list;
        setSpeakingIx(ix);
        list.forEach(u => synth.speak(u));
    }

    const onInputKey = (e) => {
        // Enter wysyła, Shift+Enter nowa linia
        if(e.key === "Enter" && !e.shiftKey)
        {
            e.preventDefault();
            send();
        }
    }


    // odświeżenie widoku panelu bez przeładowania strony (rozmowa z asystentem zostaje);
    // gdy rodzic nie udostępnia refreshContent – pełne przeładowanie
    const refreshPanel = () => {
        setRefreshNeeded(false);
        if(props.mainView && typeof props.mainView.refreshContent === "function") props.mainView.refreshContent();
        else window.location.reload();
    }

    // okno "wymagane odświeżenie" – nad oknem asystenta (dialog panelu ma niższy z-index)
    const refreshDialog = refreshNeeded &&
        <div className="ai_refresh_overlay">
            <div className="ai_refresh" role="alertdialog" aria-label="Aktualizacja danych">
                <div className="ai_refresh_title">Aktualizacja danych</div>
                <div className="ai_refresh_msg">
                    Nastąpiła aktualizacja danych w panelu, wymagane odświeżenie strony.
                </div>
                <div className="ai_refresh_btns">
                    <button type="button" className="ai_refresh_later" onClick={() => setRefreshNeeded(false)}>Później</button>
                    <button type="button" className="ai_refresh_ok" autoFocus onClick={refreshPanel}>Odśwież stronę</button>
                </div>
            </div>
        </div>;

    // asystent nieaktywny w placówce – bez przycisku i okna (okno "wymagane odświeżenie" zostaje, gdyby zapis zdążył się wykonać)
    if(!active) return refreshDialog || null;

    if(!open)
    {
        return (
            <>
                {/* przycisk w rogu ekranu – stały obrazek, bez animacji i podmian (nie rozprasza) */}
                <div className="ai_launcher">
                    <button type="button" className="ai_fab" title="Asystent AI" aria-label="Otwórz Asystenta AI" onClick={() => setOpen(true)}>
                        <img src={agentButtonImg} alt="Asystent AI – chętnie ci pomogę" />
                    </button>
                </div>
                {refreshDialog}
            </>
        )
    }

    return (
        <>
        <div className="ai_overlay" onMouseDown={(e) => { if(e.target === e.currentTarget) setOpen(false); }}>

            <div className="ai_dialog" role="dialog" aria-label="Asystent AI">

                <div className="ai_header">
                    <span className="ai_title">Asystent AI</span>

                    <button type="button" className="ai_btn_icon" title="Wyczyść rozmowę" disabled={busy || messages.length === 0} onClick={() => { setMessages([]); clearKsef(); }}>
                        <MdDeleteOutline />
                    </button>
                    <button type="button" className="ai_btn_icon" title="Zamknij" onClick={() => setOpen(false)}>
                        <MdClose />
                    </button>
                </div>

                {
                    usage && usage.input && usage.output &&
                    <div className="ai_usage" title="Limity tokenów asystenta AI w placówce">
                        <UsageRow label="Tokeny wejściowe" u={usage.input} />
                        <UsageRow label="Tokeny wyjściowe" u={usage.output} />
                    </div>
                }

                <div className="ai_messages" ref={listRef}>
                    {
                        messages.length === 0 &&
                        <div className="ai_empty">Zadaj pytanie, a asystent AI postara się pomóc.</div>
                    }

                    {
                        messages.map((msg, ix) =>
                            <div key={ix} className={"ai_msg ai_msg_" + msg.role + (msg.displays && msg.displays.length > 0 ? " ai_msg_wide" : "")}>
                                {
                                    msg.role === "assistant"
                                    ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
                                    : msg.content
                                }
                                {
                                    msg.role === "assistant" && ttsVoice && speechText(msg.content) !== "" &&
                                    <div className="ai_msg_tools">
                                        <button type="button" className={"ai_speak" + (speakingIx === ix ? " ai_speak_on" : "")}
                                                title={speakingIx === ix ? "Zatrzymaj czytanie" : "Przeczytaj na głos"}
                                                aria-label={speakingIx === ix ? "Zatrzymaj czytanie" : "Przeczytaj na głos"}
                                                onClick={() => toggleSpeak(ix, msg.content)}>
                                            { speakingIx === ix ? <MdStop /> : <MdVolumeUp /> }
                                            { speakingIx === ix ? "Zatrzymaj" : "Przeczytaj" }
                                        </button>
                                    </div>
                                }
                                {
                                    msg.files && msg.files.length > 0 &&
                                    <div className="ai_msg_files">
                                        { msg.files.map((f, fx) => <span key={fx} className="ai_file"><MdAttachFile /> {f.name}</span>) }
                                    </div>
                                }
                                {
                                    msg.displays && msg.displays.map((d, dx) => <AIDisplay key={dx} display={d} />)
                                }
                                {
                                    msg.attachments && msg.attachments.length > 0 &&
                                    <div className="ai_attachments">
                                        {
                                            msg.attachments.map((att, ax) =>
                                                <button key={ax} type="button" className="ai_attachment" title="Otwórz plik" onClick={() => openAttachment(att)}>
                                                    <MdPictureAsPdf /> {att.name}
                                                </button>
                                            )
                                        }
                                    </div>
                                }
                            </div>
                        )
                    }

                    {
                        busy &&
                        <div className="ai_msg ai_msg_assistant ai_typing">
                            <span></span><span></span><span></span>
                        </div>
                    }
                </div>

                {
                    (files.length > 0 || uploading > 0) &&
                    <div className="ai_files">
                        {
                            files.map((f, fx) =>
                                <span key={fx} className="ai_file">
                                    <MdAttachFile /> {f.name}
                                    <button type="button" title="Usuń załącznik" onClick={() => setFiles(list => list.filter((_, ix) => ix !== fx))}><MdClose /></button>
                                </span>
                            )
                        }
                        { uploading > 0 && <span className="ai_file ai_file_loading">Wgrywanie ({uploading})…</span> }
                    </div>
                }

                {
                    ksefOpen &&
                    <KsefPanel ksef={ksef} busy={busy || blocked}
                               onPick={onKsefFile}
                               onPass={(p) => setKsef(k => ({ ...k, pass: p }))}
                               onClear={() => setKsef(KSEF_EMPTY)}
                               onClose={() => setKsefOpen(false)}
                               onReady={() => send("Wczytałem w panelu KSeF certyfikat i klucz prywatny oraz wpisałem hasło – sprawdź je.")} />
                }

                <div className="ai_input">
                    <input ref={fileRef} type="file" multiple accept={AI_FILE_ACCEPT} style={{ display: "none" }} onChange={onPickFiles} />
                    <button type="button" className="ai_attach" title={"Załącz plik (PDF, obraz, TXT, CSV – do analizy; także Word / Excel / film – do wysłania w wiadomości lub na tablicę; maks. " + AI_FILES_MAX + ")"}
                            disabled={busy || blocked || files.length + uploading >= AI_FILES_MAX}
                            onClick={() => fileRef.current && fileRef.current.click()}>
                        <MdAttachFile />
                    </button>
                    <textarea
                        ref={inputRef}
                        rows={2}
                        placeholder={ agentOff
                                        ? "Asystent AI został wyłączony w placówce – ponowne włączenie przez administratora systemu."
                                        : limitReached
                                        ? "Wykorzystano limit tokenów asystenta AI – skontaktuj się z administratorem systemu."
                                        : "Wpisz pytanie… (Enter – wyślij, Shift+Enter – nowa linia)" }
                        value={prompt}
                        disabled={blocked}
                        onChange={(e) => setPrompt(e.target.value)}
                        onKeyDown={onInputKey}
                    />
                    <button type="button" className="ai_send" title="Wyślij" disabled={busy || blocked || uploading > 0 || (prompt.trim() === "" && files.length === 0)} onClick={send}>
                        <MdSend />
                    </button>
                </div>

                <div className="ai_privacy">
                    <MdInfoOutline className="ai_privacy_icon" />
                    <div>
                        <div>
                            <b>Dla ochrony prywatności dane osobowe</b> (np. imiona, nazwiska, adresy, numery kont) <b>są anonimizowane</b> przed przekazaniem do asystenta AI.
                            Z tego powodu wykonanie niektórych operacji może być niemożliwe.
                        </div>
                        <div className="ai_privacy_tip">
                            <b>Wskazówka:</b> dane osobowe w swoim pytaniu możesz zanonimizować samodzielnie, obejmując je funkcją <code>fA(…)</code>,
                            np. <i>„Co wiesz o dziecku <code>fA(Agnieszka Kowalska)</code>?”</i>
                        </div>
                        <div className="ai_privacy_tip">
                            <b>Uwaga:</b> treść załączonych plików jest przekazywana do asystenta AI <b>bez anonimizacji</b>
                            (z wyjątkiem certyfikatów i kluczy KSeF – te nie są przekazywane do AI).
                        </div>
                    </div>
                </div>

            </div>
        </div>
        {refreshDialog}
        </>
    )
}


export default AIDialog;
